import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Plus, ChevronLeft, ChevronRight, ArrowUpRight, ImageOff } from "lucide-react";
import { PACKAGE_SCOPES, scopeCover, summarizeScope } from "./scopes";
import CoverImage from "./CoverImage";

/**
 * The package — the answer to "what did we do for this project?" in one glance,
 * and the way into every deliverable. One card per sellable scope, each fronted
 * by a still from the work itself, on a rail that scrolls sideways.
 *
 * There is no long page behind this: a card opens its scope in a dialog. So the
 * card has to say enough on its own — what the scope is, what's on file, and
 * what happens when you click it.
 *
 * View mode: a card opens its deliverables.
 * Organize mode: every scope shows, the corner circle marks it as sold, and the
 * card still opens so work can be added — "we never sold video here" has to read
 * differently from "nobody has uploaded it".
 */
export default function PackageBoard({
  groups = {},
  included = [],
  organizing,
  onToggle,
  onOpen,
  mediaUrl,
}) {
  // Delivered work leads; scopes that were never part of this project trail
  // behind it. Organize mode keeps the canonical order so a card never jumps
  // out from under the cursor as it's toggled.
  const scopes = organizing
    ? PACKAGE_SCOPES
    : [...PACKAGE_SCOPES].sort((a, b) => rank(a, groups, included) - rank(b, groups, included));

  return (
    <div data-testid="package-board">
      <div className="flex items-baseline justify-between gap-4 mb-3">
        <h2 className="micro-label">The package</h2>
        {organizing && (
          <span className="text-xs text-muted-foreground">
            Open a card to fill it · tap the circle to mark it as sold
          </span>
        )}
      </div>

      <Rail>
        {scopes.map(scope => (
          <ScopeCard
            key={scope.key}
            scope={scope}
            group={groups[scope.key] || {}}
            inPackage={included.includes(scope.key)}
            organizing={organizing}
            onToggle={() => onToggle(scope.key)}
            onOpen={() => onOpen(scope.key)}
            mediaUrl={mediaUrl}
          />
        ))}
      </Rail>
    </div>
  );
}

const rank = (scope, groups, included) => {
  const filled = (groups[scope.key]?.all?.length || 0) > 0;
  if (filled) return 0;
  if (included.includes(scope.key)) return 1;
  return 2;
};

/* ──────────────────────────────── the rail ─────────────────────────────── */

/** A sideways rail with snap points, and arrows once it actually overflows —
 *  on a wide screen the whole package fits and the arrows stay out of it. */
function Rail({ children }) {
  const ref = useRef(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const slack = el.scrollWidth - el.clientWidth;
    setEdges({
      start: el.scrollLeft > 8,
      end: slack > 8 && el.scrollLeft < slack - 8,
    });
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, children]);

  const step = (direction) => {
    const el = ref.current;
    if (!el) return;
    // One card and its gap — the rail lands on a snap point either way.
    el.scrollBy({ left: direction * (el.clientWidth > 720 ? 384 : el.clientWidth * 0.85), behavior: "smooth" });
  };

  return (
    <div className="relative">
      <div
        ref={ref}
        onScroll={measure}
        className="flex gap-4 overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-px-4 -mx-4 px-4 md:-mx-6 md:px-6 pb-1"
        data-testid="package-rail"
      >
        {children}
      </div>

      <RailArrow side="left" show={edges.start} onClick={() => step(-1)} />
      <RailArrow side="right" show={edges.end} onClick={() => step(1)} />
    </div>
  );
}

function RailArrow({ side, show, onClick }) {
  if (!show) return null;
  const Icon = side === "left" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={side === "left" ? "Scroll back" : "Scroll forward"}
      className={`hidden md:flex absolute top-1/2 -translate-y-1/2 ${
        side === "left" ? "left-0 -translate-x-1/2" : "right-0 translate-x-1/2"
      } w-9 h-9 rounded-full glass items-center justify-center text-foreground transition-smooth hover:opacity-[0.88] active:scale-[0.975] z-10`}
      data-testid={`package-rail-${side}`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

/* ──────────────────────────────── the card ─────────────────────────────── */

function ScopeCard({ scope, group, inPackage, organizing, onToggle, onOpen, mediaUrl }) {
  const { Icon } = scope;
  const count = group.all?.length || 0;
  const filled = count > 0;

  // Three states, each with its own quiet colour: delivered, sold but nothing
  // on file yet, and never part of this project.
  let state = { text: "Not in package", className: "text-white/45" };
  if (filled) state = { text: inPackage ? "Delivered" : "On file", className: "text-green-400" };
  else if (inPackage) state = { text: "Nothing uploaded", className: "text-orange-300" };

  // Nothing to open on a scope that was never sold and holds nothing — outside
  // organize mode that card is a fact, not a door.
  const openable = filled || organizing;

  return (
    <article
      className={`group relative shrink-0 snap-start w-[82vw] max-w-[380px] sm:w-[320px] lg:w-[368px] rounded-xl overflow-hidden border border-border bg-card transition-smooth ${
        openable ? "card-hover" : "opacity-55"
      }`}
      data-testid={`package-card-${scope.key}`}
    >
      <button
        type="button"
        onClick={openable ? onOpen : undefined}
        disabled={!openable}
        className="block w-full text-left disabled:cursor-default"
        title={openable ? `${scope.cta} — ${scope.label}` : `${scope.label} wasn't part of this project`}
        data-testid={`package-open-${scope.key}`}
      >
        {/* Landscape, because everything behind these cards is: a render is 3:2,
            a film and a website screenshot are 16:9. A portrait frame would crop
            half the width off the one image the card exists to show. */}
        <div className="relative aspect-[4/3] bg-zinc-900 overflow-hidden">
          <CardCover group={group} scope={scope} mediaUrl={mediaUrl} />

          {/* The scrim the copy sits on. Ramped in many steps rather than a few
              — over a bright still a coarse gradient reads as a black band laid
              across the image instead of the image falling into shadow. It has
              to hold white type over a sunlit render, so it stays heavy through
              the bottom 40% and only then lets the image back. */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to top, rgba(0,0,0,.96) 0%, rgba(0,0,0,.93) 10%, rgba(0,0,0,.88) 20%, rgba(0,0,0,.80) 30%, rgba(0,0,0,.68) 40%, rgba(0,0,0,.50) 50%, rgba(0,0,0,.32) 62%, rgba(0,0,0,.16) 75%, rgba(0,0,0,.05) 88%, rgba(0,0,0,0) 100%)",
            }}
          />

          <span className="absolute top-4 left-4">
            <Icon className="w-4 h-4 text-white/70" strokeWidth={1.5} />
          </span>

          <div className="absolute inset-x-0 bottom-0 px-5 pb-5">
            {/* The scrim carries most of the legibility; the shadow covers the
                rest, so a sunlit render can stay bright instead of being dimmed
                until the type happens to read. Not on the button — a dark halo
                behind black-on-white just looks smudged. */}
            <div style={{ textShadow: "0 1px 10px rgba(0,0,0,.5)" }}>
              <span className={`micro-label ${state.className}`}>{state.text}</span>
              <p className="text-white text-lg font-light tracking-[-0.4px] mt-1">{scope.label}</p>
              <p className="text-white/55 text-xs mt-0.5 truncate">
                {filled ? summarizeScope(scope, group) : scope.blurb}
              </p>
            </div>

            {openable && (
              <span className="mt-3.5 inline-flex items-center gap-2 h-9 px-4 rounded-full bg-white text-black text-sm tracking-[-0.25px] transition-smooth group-hover:opacity-[0.88]">
                {filled ? scope.cta : "Add work"}
                <ArrowUpRight className="w-3.5 h-3.5" />
              </span>
            )}
          </div>
        </div>
      </button>

      {organizing && (
        <button
          type="button"
          onClick={onToggle}
          title={`Mark ${scope.label} as ${inPackage ? "not sold" : "sold"}`}
          className="absolute top-3 right-3 w-7 h-7 rounded-full glass flex items-center justify-center transition-smooth hover:opacity-[0.88] active:scale-[0.975] z-10"
          data-testid={`package-toggle-${scope.key}`}
        >
          <span
            className={`w-4 h-4 rounded-full border flex items-center justify-center transition-smooth ${
              inPackage ? "bg-primary border-primary text-primary-foreground" : "border-white/60"
            }`}
          >
            {inPackage ? <Check className="w-2.5 h-2.5" /> : <Plus className="w-2.5 h-2.5 text-white/70" />}
          </span>
        </button>
      )}
    </article>
  );
}

/** The card's face: a still from the work, a video poster, or — when there's
 *  nothing on file — the scope's own mark on an empty frame. */
function CardCover({ group, scope, mediaUrl }) {
  const cover = scopeCover(group);
  const sources = cover.media ? [mediaUrl(cover.media, "thumb")] : cover.urls || [];

  return (
    <CoverImage
      sources={sources}
      className="w-full h-full object-cover transition-transform duration-850 ease-brand group-hover:scale-[1.04]"
      fallback={
        /* A deck or a live platform often has no still of its own. An empty
           black frame reads as broken, so the scope's own mark sits on a quiet
           wash — clearly no image, but composed rather than missing. */
        <span
          className="w-full h-full flex items-center justify-center"
          style={{ background: "linear-gradient(150deg, #1b1b1b 0%, #101010 55%, #191713 100%)" }}
        >
          {sources.length ? (
            <ImageOff className="w-7 h-7 text-white/20" strokeWidth={1.25} />
          ) : (
            <scope.Icon className="w-9 h-9 text-white/20" strokeWidth={0.9} />
          )}
        </span>
      }
    />
  );
}
